import { createQuestion, fmt, selectTemplates } from '../questionUtils.js';

export function buildCircularQuestions({ params, rng, difficultyLevel, exerciseCount, thinkingCount }) {
  const R = params.radius;
  const m = params.mass;
  const g = params.gravity;
  const v0 = params.initialSpeed;
  const v0Question = Math.max(v0, Math.sqrt(5 * g * R) + 0.8);
  const vTop = Math.sqrt(Math.max(0, v0Question * v0Question - 4 * g * R));
  const anBottom = (v0Question * v0Question) / R;
  const fcBottom = m * anBottom;
  const nBottom = m * (g + (v0Question * v0Question) / R);
  const nTop = m * ((vTop * vTop) / R - g);
  const criticalRod = Math.sqrt(4 * g * R);
  const criticalString = Math.sqrt(5 * g * R);
  const stringOk = v0 >= criticalString;
  const omegaBottom = v0 / R;

  const exercises = [
    {
      level: 1,
      build: (index) =>
        createQuestion({
          id: `circular-ex-${index}`,
          type: 'exercise',
          level: 1,
          topic: '向心加速度与向心力',
          prompt: `质量 $m=${fmt(m, 1)}\\ \\mathrm{kg}$ 的小球在半径 $R=${fmt(R, 2)}\\ \\mathrm{m}$ 的竖直圆轨道最低点，速度为 $v_0=${fmt(v0Question, 1)}\\ \\mathrm{m/s}$。求此时向心加速度 $a_n$ 和向心力 $F_n$。`,
          formula: 'a_n=\\dfrac{v_0^2}{R},\\quad F_n=\\dfrac{mv_0^2}{R}',
          answer: `$a_n\\approx ${fmt(anBottom, 2)}\\ \\mathrm{m/s^2}$，$F_n\\approx ${fmt(fcBottom, 2)}\\ \\mathrm{N}$`,
          answerValue: fcBottom,
          unit: 'N',
          solution: [
            `向心加速度：$a_n=\\dfrac{v_0^2}{R}=\\dfrac{${fmt(v0Question, 1)}^2}{${fmt(R, 2)}}\\approx ${fmt(anBottom, 2)}\\ \\mathrm{m/s^2}$。`,
            `向心力：$F_n=ma_n=${fmt(m, 1)}\\times${fmt(anBottom, 2)}\\approx ${fmt(fcBottom, 2)}\\ \\mathrm{N}$，方向指向圆心。`,
            '注意：向心力是效果力，由轨道支持力和重力沿半径方向的合力提供，并不是一个新的性质力。',
          ],
          knowledge: ['向心加速度', '向心力', '圆周运动'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `circular-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '最高点速度',
          prompt: `半径 $R=${fmt(R, 2)}\\ \\mathrm{m}$ 的竖直圆轨道，小球在最低点速度为 $v_0=${fmt(v0Question, 1)}\\ \\mathrm{m/s}$，$g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$。忽略阻力，求小球到达最高点时的速度 $v_{\\mathrm{top}}$。`,
          formula: 'v_{\\mathrm{top}}^2=v_0^2-4gR',
          answer: `$v_{\\mathrm{top}}\\approx ${fmt(vTop, 2)}\\ \\mathrm{m/s}$`,
          answerValue: vTop,
          unit: 'm/s',
          solution: [
            '最低点到最高点高度差为 $2R$，只有重力做功，机械能守恒。',
            `$\\tfrac12mv_0^2=\\tfrac12mv_{\\mathrm{top}}^2+mg(2R)$。`,
            `$v_{\\mathrm{top}}=\\sqrt{${fmt(v0Question, 1)}^2-4\\times${fmt(g, 1)}\\times${fmt(R, 2)}}\\approx ${fmt(vTop, 2)}\\ \\mathrm{m/s}$。`,
          ],
          knowledge: ['机械能守恒', '竖直圆周运动'],
        }),
    },
    {
      level: 3,
      build: (index) =>
        createQuestion({
          id: `circular-ex-${index}`,
          type: 'exercise',
          level: 3,
          topic: '最高点杆作用力',
          prompt: `质量 $m=${fmt(m, 1)}\\ \\mathrm{kg}$ 的小球在半径 $R=${fmt(R, 2)}\\ \\mathrm{m}$ 的竖直圆轨道（轻杆模型）内运动，最高点速度 $v=${fmt(vTop, 1)}\\ \\mathrm{m/s}$，$g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$。求轨道/杆对小球的作用力 $N$，并说明方向。`,
          formula: 'N=\\dfrac{mv^2}{R}-mg',
          answer: `$N\\approx ${fmt(nTop, 2)}\\ \\mathrm{N}$，${nTop >= 0 ? '方向指向圆心（杆对球施加拉力）' : '$N$ 为负，杆对球施加背离圆心的支持力'}`,
          answerValue: nTop,
          unit: 'N',
          solution: [
            '在最高点，取指向圆心（竖直向下）为正方向：$N+mg=\\dfrac{mv^2}{R}$。',
            `$N=\\dfrac{mv^2}{R}-mg=\\dfrac{${fmt(m, 1)}\\times${fmt(vTop, 1)}^2}{${fmt(R, 2)}}-${fmt(m, 1)}\\times${fmt(g, 1)}$。`,
            `$N\\approx ${fmt(nTop, 2)}\\ \\mathrm{N}$。${nTop >= 0 ? '为正，说明杆对球的作用力指向圆心。' : '为负，说明杆对球的作用力背离圆心，杆处于“支撑”状态。'}`,
          ],
          knowledge: ['竖直圆周运动', '向心力方程', '轻杆模型'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `circular-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '最低点轨道作用力',
          prompt: `质量 $m=${fmt(m, 1)}\\ \\mathrm{kg}$ 的小球在半径 $R=${fmt(R, 2)}\\ \\mathrm{m}$ 的竖直圆轨道最低点，速度 $v_0=${fmt(v0Question, 1)}\\ \\mathrm{m/s}$。求轨道对小球的支持力 $N$（$g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$）。`,
          formula: 'N=mg+\\dfrac{mv_0^2}{R}',
          answer: `$N\\approx ${fmt(nBottom, 2)}\\ \\mathrm{N}$，方向竖直向上（指向圆心）`,
          answerValue: nBottom,
          unit: 'N',
          solution: [
            '在最低点，支持力向上，重力向下，合力提供指向圆心的向心力。',
            `$N-mg=\\dfrac{mv_0^2}{R}$，所以 $N=mg+\\dfrac{mv_0^2}{R}$。`,
            `$N=${fmt(m, 1)}\\times${fmt(g, 1)}+\\dfrac{${fmt(m, 1)}\\times${fmt(v0Question, 1)}^2}{${fmt(R, 2)}}\\approx ${fmt(nBottom, 2)}\\ \\mathrm{N}$。`,
          ],
          knowledge: ['向心力', '最低点受力', '牛顿第二定律'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `circular-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '竖直圆周运动的临界速度',
          prompt: `竖直圆轨道半径 $R=${fmt(R, 2)}\\ \\mathrm{m}$，$g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$。分别求轻杆模型和轻绳模型中小球在最低点所需的最小速度。`,
          formula: 'v_{\\text{杆,min}}=2\\sqrt{gR},\\quad v_{\\text{绳,min}}=\\sqrt{5gR}',
          answer: `轻杆：$v_{\\min}\\approx ${fmt(criticalRod, 2)}\\ \\mathrm{m/s}$；轻绳：$v_{\\min}\\approx ${fmt(criticalString, 2)}\\ \\mathrm{m/s}$`,
          solution: [
            '轻杆可提供支持力，小球只需能到达最高点（速度可趋近于 0）：$v_0^2=4gR$，所以 $v_{0,\\min}=2\\sqrt{gR}$。',
            '轻绳只能提供拉力，最高点需满足 $mg=\\dfrac{mv_{\\mathrm{top}}^2}{R}$，即 $v_{\\mathrm{top}}\\ge\\sqrt{gR}$。',
            '结合能量守恒 $v_0^2=v_{\\mathrm{top}}^2+4gR$，得 $v_{0,\\min}=\\sqrt{5gR}$。',
            `代入 $R=${fmt(R, 2)}\\ \\mathrm{m}$、$g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$：轻杆约 ${fmt(criticalRod, 2)} m/s，轻绳约 ${fmt(criticalString, 2)} m/s。`,
          ],
          knowledge: ['轻杆与轻绳', '临界速度', '机械能守恒'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `circular-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '完整圆周的条件判断',
          prompt: `半径 $R=${fmt(R, 2)}\\ \\mathrm{m}$，$g=${fmt(g, 1)}\\ \\mathrm{m/s^2}$。若小球在最低点的速度为 $v_0=${fmt(v0, 1)}\\ \\mathrm{m/s}$，在轻绳模型下能否完整通过最高点？请通过计算说明。`,
          formula: 'v_0\\ge\\sqrt{5gR}',
          answer: stringOk
            ? `能。$v_0=${fmt(v0, 1)}\\ \\mathrm{m/s}\\ge\\sqrt{5gR}\\approx ${fmt(criticalString, 2)}\\ \\mathrm{m/s}$。`
            : `不能。$v_0=${fmt(v0, 1)}\\ \\mathrm{m/s}<\\sqrt{5gR}\\approx ${fmt(criticalString, 2)}\\ \\mathrm{m/s}$，绳会在最高点前松弛。`,
          solution: [
            `轻绳模型完整通过最高点的临界速度 $v_{0,\\min}=\\sqrt{5gR}\\approx ${fmt(criticalString, 2)}\\ \\mathrm{m/s}$。`,
            `本题 $v_0=${fmt(v0, 2)}\\ \\mathrm{m/s}$，${stringOk ? '大于临界值，因此可以完整通过最高点。' : '小于临界值，因此不能完整通过最高点。'}`,
            `最低点角速度 $\\omega_0=v_0/R\\approx ${fmt(omegaBottom, 2)}\\ \\mathrm{rad/s}$。`,
          ],
          knowledge: ['临界条件', '轻绳模型', '机械能守恒'],
        }),
    },
  ];

  const thinking = [
    {
      level: 1,
      build: (index) =>
        createQuestion({
          id: `circular-th-${index}`,
          type: 'thinking',
          level: 1,
          topic: '向心力的来源',
          prompt: '做匀速圆周运动的物体一定受到“向心力”这个力吗？请说明向心力的本质。',
          formula: 'F_n=\\dfrac{mv^2}{R}',
          answer:
            '不一定存在一个名为“向心力”的单独力。向心力是按效果命名的合力，由重力、支持力、摩擦力、绳张力等实际力沿半径方向的合力提供。',
          solution: [
            '向心力不是新的性质力，而是指向圆心的合力。',
            '例如：绳系小球靠绳张力提供；汽车转弯靠静摩擦力提供；行星运动靠万有引力提供。',
            '受力分析时只画实际力，再把这些力沿半径方向合成，得到向心力。',
          ],
          knowledge: ['向心力', '受力分析', '效果力'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `circular-th-${index}`,
          type: 'thinking',
          level: 2,
          topic: '轻杆与轻绳的区别',
          prompt: '在竖直圆周运动中，轻杆模型和轻绳模型在最高点的临界条件有什么不同？为什么会有这种差别？',
          formula: 'N_{\\text{杆}}\\in\\mathbb{R},\\quad N_{\\text{绳}}\\ge0',
          answer:
            '轻杆可以提供拉力或支持力，最高点速度可趋近于 0，临界最低点速度为 2√(gR)；轻绳只能提供拉力，最高点至少需要重力提供全部向心力，临界速度为 √(5gR)。',
          solution: [
            '轻绳只能拉、不能推，最高点必须满足 $mg+T=\\dfrac{mv^2}{R}$ 且 $T\\ge0$，所以 $v_{\\mathrm{top}}\\ge\\sqrt{gR}$。',
            '轻杆既能拉也能推，最高点速度可以小于 $\\sqrt{gR}$，甚至可以趋近于 0（杆提供向上的支持力）。',
            '再结合机械能守恒，分别得到最低点临界速度 $2\\sqrt{gR}$ 和 $\\sqrt{5gR}$。',
          ],
          knowledge: ['轻杆模型', '轻绳模型', '临界条件'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `circular-th-${index}`,
          type: 'thinking',
          level: 2,
          topic: '变速圆周运动',
          prompt: '竖直圆周运动中小球的速度大小不断变化，它是否仍然具有向心加速度？切向加速度由什么提供？',
          formula: 'a_n=\\dfrac{v^2}{R},\\quad a_t=-g\\sin\\theta',
          answer:
            '仍然具有向心加速度，因为速度方向不断改变；切向加速度由重力沿切线方向的分量提供，改变速度大小。',
          solution: [
            '只要做曲线运动，速度方向就改变，因此存在指向圆心的法向加速度 $a_n=v^2/R$。',
            '沿切线方向，重力分量 $mg\\sin\\theta$ 产生切向加速度 $a_t=-g\\sin\\theta$（$\\theta$ 从最低点量起）。',
            '在最低点和最高点，切向加速度为零，但向心加速度不为零；在其他位置两者同时存在。',
          ],
          knowledge: ['变速圆周运动', '切向加速度', '法向加速度'],
        }),
    },
    {
      level: 3,
      build: (index) =>
        createQuestion({
          id: `circular-th-${index}`,
          type: 'thinking',
          level: 3,
          topic: '能量与向心力的关系',
          prompt: '有同学认为“向心力不做功，所以圆周运动机械能一定守恒”。这种说法是否正确？请举出反例并说明理由。',
          formula: 'W_{F_n}=0',
          answer:
            '不正确。向心力始终垂直于速度，确实不做功，但机械能是否守恒取决于其他力是否做功。竖直圆周运动中重力做功，动能与势能相互转化，但机械能可守恒；若有摩擦或阻尼，机械能会减少。',
          solution: [
            '向心力方向始终指向圆心，与速度垂直，因此向心力不做功，不改变动能。',
            '但机械能守恒的条件是“只有重力或弹力做功”，不是“向心力不做功”。',
            '竖直圆周运动中重力做功使动能和势能转化；若存在摩擦或空气阻力，机械能转化为内能，机械能不守恒。',
            '所以该说法混淆了“向心力不做功”和“系统机械能守恒”的条件。',
          ],
          knowledge: ['功与能', '向心力做功', '机械能守恒条件'],
        }),
    },
  ];

  const selectedExercises = selectTemplates(rng, exercises, exerciseCount, difficultyLevel);
  const selectedThinking = selectTemplates(rng, thinking, thinkingCount, difficultyLevel);
  return {
    exercises: selectedExercises.map((template, index) => template.build(index + 1)),
    thinking: selectedThinking.map((template, index) => template.build(index + 1)),
  };
}
